import { INcpersPriorPensionInputs, NCPERSPriorPensionCalculations } from 'app/calcs/customers/ncpers/prior';
import { defaultMonthlyPensionPayment, FormOfPaymentOptions } from 'app/calcs/pensions';
import { AllFormsOfPayment, IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces';
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector';
import { MaterialNumber } from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment';
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps } from 'app/containers/PipelineSection/QuestionGroupContainer';
import { IRetirementPerson, Profile } from 'app/redux/Profile/model';
import * as utils from 'app/utils';
import { getBrandHelpers, getBrandInfo } from 'config'
import * as _ from 'lodash';
import * as moment from 'moment';
import * as React from 'react';
import { Col, FormGroup, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { Infobox } from '../../../../components/Infobox';

interface IPriorPensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson,
  ncpersPrior: INcpersPriorPensionInputs,
  monthlyPayment: IMonthlyPensionPayments,
  pensionAmount: number,
  isFromNonCoveredEmployment: boolean,
  kind: number,
  beginPaymentsAge: moment.Duration
}

const defaultBeginPaymentsAge = moment.duration(55, 'years')
const getMainTitle = () => getBrandInfo().priorPensionTitle || 'Prior Pension'

class PriorPensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, IPriorPensionContainerState> {
  priorPensionAmountNS = 'pension.ncpersPrior.pensionAmount'
  priorIsFromNonCoveredEmploymentNS = 'pension.ncpersPrior.isFromNonCoveredEmployment'
  priorKindNS = 'pension.ncpersPrior.kind'
  priorBeginPaymentsAgeNS = 'pension.ncpersPrior.beginPaymentsAge'

  state: IPriorPensionContainerState = {
    profile: null,
    ncpersPrior: null,
    monthlyPayment: _.clone(defaultMonthlyPensionPayment),
    headOfHousehold: null,
    pensionAmount: null,
    isFromNonCoveredEmployment: null,
    kind: null,
    beginPaymentsAge: null
  }

  debounceCalculations = _.debounce((profile: Profile) => {
    const { ncpersPrior } = profile.pension
    const computer = new NCPERSPriorPensionCalculations(profile)
    const monthlyPayment = computer.calculateSimplePension(utils.todaysDate(), ncpersPrior.pensionAmount, ncpersPrior.kind, true)
    if (!_.isEqual(this.state.monthlyPayment, monthlyPayment)) {
      this.setState({ monthlyPayment, ncpersPrior })
    }
  }, 300)

  initCalc = false

  componentWillMount() {
    this.state.pensionAmount = this.getValueForQuestion(this.priorPensionAmountNS) || 0
    this.state.isFromNonCoveredEmployment = this.getValueForQuestion(this.priorIsFromNonCoveredEmploymentNS)
    this.state.kind = this.getValueForQuestion(this.priorKindNS) || 0
    this.state.beginPaymentsAge = this.getValueForQuestion(this.priorBeginPaymentsAgeNS) || defaultBeginPaymentsAge

    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info') || null
      if (!profile) { return }
      const headOfHousehold = profile.getHeadOfHousehold()

      // if there is no ncpersPrior pension,make one now
      if (!profile.pension.ncpersPrior) {
        // tslint:disable-next-line:no-object-literal-type-assertion
        profile.pension.ncpersPrior = {
          beginPaymentsAge: moment.duration(65, 'years'),
          kind: SimplePensionFormOfPayment.fullJointAndSurvivor
        } as INcpersPriorPensionInputs
      }

      this.debounceCalculations(profile);

      const ncpersPrior = profile.pension.ncpersPrior
      this.setState({ profile, headOfHousehold, ncpersPrior })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: IPriorPensionContainerState) {
    this.updateCalculations(nextState)
  }

  updateCalculations(nextState: IPriorPensionContainerState) {
    const { profile, headOfHousehold, pensionAmount, kind, isFromNonCoveredEmployment } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const newNcpersPrior = {
      pensionAmount,
      kind,
      isFromNonCoveredEmployment,
      beginPaymentsAge: nextState.beginPaymentsAge
    }

    // tslint:disable-next-line:no-console
    //console.log('updateCalcs: ' + JSON.stringify({ initCalc: this.initCalc, curNcpers: profile.pension.ncpersPrior, newNcpersPrior }))

    if (!this.initCalc || !_.isEqual(newNcpersPrior, profile.pension.ncpersPrior)) {
      this.initCalc = true

      profile.pension.ncpersPrior = newNcpersPrior
      this.debounceCalculations(profile)
    }
  }

  updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.ncpersPrior.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  render() {
    if (null === this.state.ncpersPrior) { return null }
    const profile = this.state.profile
    const spouseIsValid = profile.spouseIsValid()

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.ncpersPrior.kind)
      .label

    const beginPaymentsAgeText = this.state.beginPaymentsAge ? utils.toNiceAge(this.state.beginPaymentsAge) : 'n/a'
    const monthlyPaymentAmountText = (this.state.monthlyPayment && this.state.monthlyPayment.amount) ? utils.toCurrency(this.state.monthlyPayment.amount) : 'n/a'
    const showSummary = 0 !== this.state.monthlyPayment.amount

    const findFop = (e: AllFormsOfPayment) => _.some(FormOfPaymentOptions, (q) => q.value === e && (spouseIsValid || !q.needsSpouse));
    const allowedFormsOfPayment = _.filter([
      SimplePensionFormOfPayment.singleLifeOnly,
      SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
      SimplePensionFormOfPayment.seventyFivePercentJointAndSurvivor,
      SimplePensionFormOfPayment.fullJointAndSurvivor
    ], findFop)

    const tooltipStyles = {
      fontSize: '0.75em'
    }

    const aStyle = {
      color: 'black'
    }

    const bStyle = {
      position: 'relative' as 'relative',
      top: '-2.9vw',
      left: '2vw',
      fontSize: '1.5vw',
    }

    return (
      <section className="animated fadeIn">
        <h1>{getMainTitle()}</h1>
        <h5 className="lead">Please input information about any pension you may have earned with a prior employer.</h5>
        <Row>
          <Col md={6}>

            <FormGroup>
              <RetirementAgeSelector min={55} max={70}
                placeholder="What age will you begin payments?"
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
            </FormGroup>

            <FormGroup  className="priorPensionAmount">
              <MaterialNumber
                placeholder="What is the monthly benefit payable from the prior employer's plan?"
                formattedNumberMode
                name={this.priorPensionAmountNS}
                value={this.state.pensionAmount}
                onChange={(e) => this.setStateAndSave({ pensionAmount: this.toNumber(e.currentTarget.value) })}
              />
            </FormGroup>

            {getBrandHelpers().showNonCoveredPriorPensionLanguage && <FormGroup>
              <div className="material-checkbox-group hasValue">
                <label className="material-input-placeholder">Is This Pension From Non-covered Employment?
                  <Infobox>
                      Non-covered employment is employment that is not covered by Social Security. For more information, please see:
                      <br/><span style={tooltipStyles}><a href="http://www.ssa.gov/planners/retire/wep.html" target="_blank">www.ssa.gov/planners/retire/wep.html</a></span>
                  </Infobox>
                </label>
                <input type="checkbox"
                  className="form-control material-input"
                  checked={this.state.isFromNonCoveredEmployment}
                  onChange={(e) => this.setStateAndSave({ isFromNonCoveredEmployment: e.currentTarget.checked })}
                  name={this.priorIsFromNonCoveredEmploymentNS} />
                <div style={bStyle}>
                  {this.state.isFromNonCoveredEmployment ? 'yes, it is' : 'no, it isn\'t'}
                </div>
              </div>
            </FormGroup>}

            <FormGroup>
              <FormOfPayment
                name={this.priorKindNS}
                defaultValue={this.state.kind}
                options={allowedFormsOfPayment}
                onChange={(kind) => this.setStateAndSave({ kind })} />
            </FormGroup>

          </Col>
          <Col md={6}>
            {showSummary &&
            <h2 className="animated fadeIn">If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
              in the form of&nbsp;
              <a>{currentFormOfPayment}</a>,&nbsp;
              the monthly amount will be <a>{monthlyPaymentAmountText}</a>.
            </h2>
            }
          </Col>
        </Row>
      </section>
    )
  }
}

const PriorPensionQuestionsGroup = () => {
  return (
    <PriorPensionContainer
      header={getMainTitle()} icon="" />
  )
}

export { PriorPensionQuestionsGroup, };



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Pension/PriorPension/index.tsx